import { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeCategory, setActiveCategory] = useState(slug || 'romance');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const filteredComics = useMemo(() => {
    let result = comics;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.title.toLowerCase().includes(q) ||
        c.author.toLowerCase().includes(q) ||
        c.tags.some(t => t.toLowerCase().includes(q))
      );
    } else if (activeCategory && activeCategory !== 'all') {
      result = result.filter(c => c.category === activeCategory);
    }
    return result;
  }, [activeCategory, searchQuery]);

  const totalPages = Math.ceil(filteredComics.length / itemsPerPage);
  const paginatedComics = filteredComics.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const currentCategory = categories.find(c => c.slug === activeCategory);

  const pageTitle = searchQuery
    ? `搜索"${searchQuery}"的结果 - 韩国漫画网`
    : activeCategory === 'all'
    ? '全部韩国漫画 - 韩国漫画网'
    : `${currentCategory?.name || '分类'} - 韩国漫画网`;

  const pageDescription = searchQuery
    ? `在韩国漫画网搜索"${searchQuery}"的结果，发现更多好看韩国漫画。`
    : activeCategory === 'all'
    ? '浏览全部韩国漫画资源，发现众多好看韩国漫画，支持韩漫在线无删减下拉式免费阅读。'
    : `浏览${currentCategory?.name || ''}韩国漫画，${currentCategory?.description || '发现更多好看韩国漫画'}。支持韩漫在线无删减下拉式免费阅读。`;

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": pageTitle,
    "description": pageDescription,
    "url": `https://www.koreanmanhwa.com/category/${activeCategory}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "韩国漫画网",
      "url": "https://www.koreanmanhwa.com"
    }
  };

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={`韩国漫画,${currentCategory?.name || ''},韩漫在线,免费阅读`} />
      <link rel="canonical" href={`https://www.koreanmanhwa.com/category/${activeCategory}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://www.koreanmanhwa.com/category/${activeCategory}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory?.name || '漫画分类' },
              ]}
            />
            <div className="mt-6">
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-1">
                {searchQuery ? `搜索"${searchQuery}"的结果` : currentCategory?.name || '漫画分类'}
              </h1>
              <p className="text-sm text-foreground-500">
                {searchQuery && `找到 ${filteredComics.length} 部相关漫画`}
              </p>
            </div>
            <div className="mt-6 flex items-center gap-2 flex-wrap bg-white rounded-full p-1 border border-background-200/70 shadow-sm">
              <button
                onClick={() => { setActiveCategory('all'); setCurrentPage(1); }}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${activeCategory === 'all' ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-50'}`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => { setActiveCategory(cat.slug); setCurrentPage(1); }}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${activeCategory === cat.slug ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-50'}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            <div className="mt-6">
              {paginatedComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {paginatedComics.map((comic) => (
                    <ComicCard key={comic.id} {...comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <i className="ri-emotion-sad-line text-5xl text-foreground-300 w-12 h-12 flex items-center justify-center mx-auto"></i>
                  <p className="mt-4 text-foreground-500">没有找到相关漫画</p>
                  <p className="text-sm text-foreground-400 mt-1">试试其他关键词或分类吧</p>
                </div>
              )}
            </div>
            {totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200 text-foreground-600 hover:bg-background-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line w-4 h-4 flex items-center justify-center"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer ${page === currentPage ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-50 border border-background-200'}`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200 text-foreground-600 hover:bg-background-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                </button>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}